import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { catchError, exhaustMap, map, mergeMap, of, switchMap } from 'rxjs';
import { v4 } from 'uuid';
import { ModalWindowsService } from '../../../modals/services/modal-windows.service';
import {
    InvitationExternalIdType,
    InvitationType,
} from '../../../profile/models/enums/profile.enum';
import { InvitationService } from '../../../profile/services/invitation.service';
import { SnackBarType } from '../../../shared/components/snack-bar/snack-bar.component';
import { NOTIFICATIONS } from '../../../shared/constants';
import { BadResponseWithError } from '../../../shared/models/bad-response-with-error.interface';
import { NotificationsService } from '../../../shared/services/notifications.service';
import {
    ProfileTeamResponse,
    TeamTournamentsWithPagination,
} from '../../models/api/responses/team.response';
import { ProfileTeamsService } from '../../services/team.service';
import { TeamActions } from '../actions/actions-type.action';
import { ProfileStatistics } from '../../../profile/models/api/responses/profileGeneral.response';

@Injectable()
export class TeamEffect {
    private actions$ = inject(Actions);
    private profileTeamsService = inject(ProfileTeamsService);
    private modalService = inject(ModalWindowsService);
    private notificationsService = inject(NotificationsService);
    private invitationService = inject(InvitationService);
    private translationService = inject(TranslateService);

    invitePlayer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TeamActions.profileInvitePlayerToTeam),
            exhaustMap((action) =>
                this.invitationService
                    .sendInvitations({
                        invitees: action.payload.ids,
                        targetType: InvitationType.PlayerTeam,
                        keyValues: [
                            {
                                externalIdType: InvitationExternalIdType.TeamId,
                                externalId: action.payload.teamId,
                            },
                        ],
                        requestId: v4(),
                        isTournamentV2: false,
                    })
                    .pipe(
                        mergeMap((response: unknown) => {
                            this.notificationsService.openSnackBar({
                              title: this.translationService.instant(NOTIFICATIONS.TEAM_MEMBERS_INVITED),
                              type: SnackBarType.Success,
                            });

                            return [
                                TeamActions.profileGetTeamInfo({
                                    payload: action.payload.teamId,
                                }),
                                TeamActions.profileInvitePlayerToTeamSuccess(),
                            ];
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: 'Error',
                                message: response.error.message,
                                type: SnackBarType.Error,
                                error: response.error,
                            });

                            return of(
                                TeamActions.profileInvitePlayerToTeamFailure({
                                    payload: response,
                                })
                            );
                        })
                    )
            )
        )
    );

    removePlayer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TeamActions.profileRemoveMember),
            exhaustMap((action) =>
                this.profileTeamsService
                    .removeMember(
                        action.payload.teamId,
                        action.payload.profileId
                    )
                    .pipe(
                        mergeMap(() => [
                            TeamActions.profileRemoveMemberSuccess(),
                            TeamActions.profileGetTeamInfo({
                                payload: action.payload.teamId,
                            }),
                        ]),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: 'Error',
                                message: response.error.message,
                                type: SnackBarType.Error,
                                error: response.error,
                            });

                            return of(
                                TeamActions.profileRemoveMemberFailure({
                                    payload: response,
                                })
                            );
                        })
                    )
            )
        )
    );

    leaveTeam$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TeamActions.profileLeaveTeam),
            exhaustMap((action) =>
                this.profileTeamsService.leaveTeam(action.payload.teamId).pipe(
                    mergeMap(() => {
                        this.notificationsService.openSnackBar({
                            title: this.translationService.instant(NOTIFICATIONS.LEFT_TEAM),
                            type: SnackBarType.Success,
                        });

                        return [
                            TeamActions.profileLeaveTeamSuccess(),
                            TeamActions.profileGetTeamInfo({
                                payload: action.payload.teamId,
                            }),
                        ];
                    }),
                    catchError((response: BadResponseWithError) => {
                        this.notificationsService.openSnackBar({
                            title: 'Error',
                            message: response.error.message,
                            type: SnackBarType.Error,
                            error: response.error,
                        });

                        return of(
                            TeamActions.profileLeaveTeamFailure({
                                payload: response,
                            })
                        );
                    })
                )
            )
        )
    );

    getTeam$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TeamActions.profileGetTeamInfo),
            switchMap((action) =>
                this.profileTeamsService.getTeam(action.payload).pipe(
                    mergeMap((team: ProfileTeamResponse) => [
                        TeamActions.profileGetTeamInfoSuccess({
                            payload: team,
                        }),
                    ]),
                    catchError((response: BadResponseWithError) => {
                        this.notificationsService.openSnackBar({
                            title: 'Error',
                            message: response.error.message,
                            type: SnackBarType.Error,
                            error: response.error,
                        });
                        return of(
                            TeamActions.profileGetTeamInfoFailure({
                                payload: response,
                            })
                        );
                    })
                )
            )
        )
    );

    fetchTeamTournaments = createEffect(() =>
        this.actions$.pipe(
            ofType(TeamActions.fetchTeamTournaments),
            switchMap((action) =>
                this.profileTeamsService
                    .fetchTeamTournaments(
                        action.payload.teamId,
                        action.payload.page,
                        action.payload.filters
                    )
                    .pipe(
                        mergeMap(
                            (tournaments: TeamTournamentsWithPagination) => [
                                TeamActions.fetchTeamTournamentsSuccess({
                                    payload: {
                                        response: tournaments,
                                        patchExistingTournaments:
                                            action.payload
                                                .patchExistingTournaments ??
                                            false,
                                    },
                                }),
                            ]
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TeamActions.fetchTeamTournamentsFailure({
                                    payload: response,
                                })
                            )
                        )
                    )
            )
        )
    );

    transferLeadership$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TeamActions.profileTransferLeadership),
            exhaustMap((action) =>
                this.profileTeamsService
                    .transferLeadership(
                        action.payload.teamId,
                        action.payload.playerId
                    )
                    .pipe(
                        map((team: ProfileTeamResponse) => {
                            this.notificationsService.openSnackBar({
                                title: this.translationService.instant(NOTIFICATIONS.TEAM_LEADERSHIP_TRANSFERRED),
                                type: SnackBarType.Success,
                            });

                            return TeamActions.profileTransferLeadershipSuccess(
                                {
                                    payload: team,
                                }
                            );
                        }),
                        catchError((response) =>
                            of(
                                TeamActions.profileTransferLeadershipFailure({
                                    payload: response,
                                })
                            )
                        )
                    )
            )
        )
    );

    cancelTeamMemberInvitation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TeamActions.profileCancelTeamMemberInvitation),
            exhaustMap((action) =>
                this.invitationService
                    .updateStatus(
                        action.payload.invitationId,
                        action.payload.status
                    )
                    .pipe(
                        mergeMap((response) => [
                            TeamActions.profileCancelTeamMemberInvitationSuccess(),
                            TeamActions.profileGetTeamInfo({
                                payload: action.payload.teamId,
                            }),
                        ]),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TeamActions.profileCancelTeamMemberInvitationFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    updateTeam$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TeamActions.profileUpdateTeam),
            exhaustMap((action) =>
                this.profileTeamsService
                    .updateTeam(action.payload.teamId, action.payload.request)
                    .pipe(
                        map((team: ProfileTeamResponse) => {
                            this.modalService.closeCreateTeamModal();

                            this.notificationsService.openSnackBar({
                              title: this.translationService.instant(NOTIFICATIONS.TEAM_UPDATED),
                              type: SnackBarType.Success,
                            });

                            return TeamActions.profileUpdateTeamSuccess({
                                payload: team,
                            });
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: response.error.message,
                                type: SnackBarType.Error,
                                error: response.error,
                            });

                            return of(
                                TeamActions.profileUpdateTeamFailure({
                                    payload: response,
                                })
                            );
                        })
                    )
            )
        )
    );

    applyToJoinTeam$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TeamActions.profileApplyToJoin),
            exhaustMap((action) =>
                this.invitationService
                    .sendInvitations({
                        invitees: [action.payload.id],
                        targetType: InvitationType.PlayerTeam,
                        keyValues: [
                            {
                                externalIdType: InvitationExternalIdType.TeamId,
                                externalId: action.payload.teamId,
                            },
                        ],
                        isApplication: action.payload.isApplication,
                        requestId: v4(),
                        isTournamentV2: !!action.payload.isV2,
                    })
                    .pipe(
                        mergeMap((response: unknown) => {
                            this.notificationsService.openSnackBar({
                              title: this.translationService.instant(NOTIFICATIONS.APPLIED_TO_JOIN),
                              type: SnackBarType.Success,
                            });

                            return [
                                TeamActions.profileApplyToJoinSuccess(),
                                TeamActions.profileGetTeamInfo({
                                    payload: action.payload.teamId,
                                }),
                            ];
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: 'Error',
                                message: response.error.message,
                                type: SnackBarType.Error,
                                error: response.error,
                            });

                            return of(
                                TeamActions.profileApplyToJoinFailure({
                                    payload: response,
                                })
                            );
                        })
                    )
            )
        )
    );

    fetchTeamStatistics = createEffect(() =>
        this.actions$.pipe(
            ofType(TeamActions.fetchTeamStatistics),
            switchMap(({ payload }) =>
                this.profileTeamsService
                    .fetchTeamStatistics(payload.teamId)
                    .pipe(
                        map((response: ProfileStatistics) =>
                            TeamActions.fetchTeamStatisticsSuccess({
                                payload: response,
                            })
                        ),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: response.error.message,
                                type: SnackBarType.Error,
                                error: response.error,
                            });

                            return of(
                                TeamActions.fetchTeamStatisticsFailure({
                                    payload: response,
                                })
                            );
                        })
                    )
            )
        )
    );
}
